import { createContext, useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import React from "react";
import { jwtDecode } from "jwt-decode";
import roleFieldApi from "../../../api/roleFieldAssignment";
import { toast } from "react-toastify";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const logoutTimer = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const permissions = localStorage.getItem("permissions") || "[]";

    if (token) {
      try {
        const decoded = jwtDecode(token);
        const isExpired = decoded.exp * 1000 < Date.now();

        if (!isExpired) {
          const username = decoded.sub || decoded.username;

          setUser({
            token,
            username,
            permissions: JSON.parse(permissions),
          });

          setLogoutTimer(token);
        } else {
          localStorage.removeItem("token");
        }
      } catch (e) {
        console.error("Invalid token");
        localStorage.removeItem("token");
      }
    }

    setLoading(false);
  }, []);

  const getTokenExpirationDate = (token) => {
    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return null;
      return decoded.exp * 1000;
    } catch {
      return null;
    }
  };

  const login = async (token, callback) => {
    localStorage.setItem("token", token);

    const decoded = jwtDecode(token);
    const username = decoded.sub || decoded.username;

    let permissions = [];

    const response = await roleFieldApi.getMyFieldAssignments();
    if (!response.success) {
      toast.error(response.message);
    } else {
      permissions = response.data?.data?.[0]?.fieldsAssigned || [];
      localStorage.setItem("permissions", JSON.stringify(permissions));
    }

    setUser({
      token,
      username,
      permissions,
    });

    if (callback) {
      setTimeout(callback, 0);
    }

    setLogoutTimer(token);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("permissions");
    setUser(null);

    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
    }
  };

  const setLogoutTimer = (token) => {
    const expiryTime = getTokenExpirationDate(token);
    if (!expiryTime) return;

    const timeout = expiryTime - Date.now();

    if (timeout <= 0) {
      logout();
    } else {
      logoutTimer.current = setTimeout(() => {
        logout();
        alert("Session expired. Please log in again.");
      }, timeout);
    }
  };

  return (
      <AuthContext.Provider value={{ user, login, logout, loading }}>
        {children}
      </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);